import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Star, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { useHomepage } from '../../../hooks/useHomepage';
import { useTranslation } from 'react-i18next';

export const HeroSection = () => {
    const { i18n } = useTranslation('shop');
    const isRTL = i18n.language === 'ar';

    const { data: homepageData } = useHomepage();
    const SLIDES = homepageData?.hero?.banners || [];
    const TRUST_STATS = homepageData?.hero?.stats || [];

    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        if (!SLIDES || SLIDES.length <= 1) return;
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [SLIDES.length]);

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);

    if (SLIDES.length === 0) return null;

    // In RTL the "back" arrow is ChevronRight and "forward" is ChevronLeft (visually reversed)
    const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
    const NextIcon = isRTL ? ChevronLeft : ChevronRight;
    const CtaArrow = isRTL ? ArrowLeft : ArrowRight;

    return (
        <section className="relative w-full min-h-[calc(100vh-100px)] lg:h-screen bg-[#FDFCF8] overflow-hidden flex flex-col group">

            {/* Background Slides */}
            {SLIDES.map((slide, index) => (
                <div
                    key={slide.id}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 -z-10'}`}
                >
                    {/* Mobile background */}
                    <div className="lg:hidden absolute inset-0">
                        <div className="h-[45vh] w-full relative overflow-hidden rounded-b-[2.5rem]">
                            <img
                                src={slide.image}
                                alt={slide.title}
                                className="w-full h-full object-cover"
                                loading={index === 0 ? 'eager' : 'lazy'}
                                decoding={index === 0 ? 'sync' : 'async'}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#FDFCF8] via-transparent to-transparent"></div>
                        </div>
                    </div>

                    {/* Desktop image — right side in LTR, left side in RTL */}
                    <div
                        className="hidden lg:block absolute top-0 w-[45%] h-full bg-secondary"
                        style={{ [isRTL ? 'left' : 'right']: 0 }}
                    >
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover opacity-90"
                            loading={index === 0 ? 'eager' : 'lazy'}
                            decoding={index === 0 ? 'sync' : 'async'}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-secondary via-transparent to-transparent opacity-40"></div>
                    </div>
                </div>
            ))}

            {/* Content Layer */}
            <div className="relative z-10 w-full h-full flex flex-col lg:flex-row">

                {/* Mobile Top Spacer + Indicators */}
                <div className="lg:hidden h-[45vh] w-full relative pointer-events-none flex flex-col justify-end pb-8 items-center">
                    {SLIDES[currentSlide].badge && (
                        <div
                            className="absolute bottom-16 bg-white/20 backdrop-blur-md border border-white/30 text-white px-4 py-2 rounded-full text-xs font-medium tracking-wider flex items-center gap-2 shadow-lg"
                            style={{ [isRTL ? 'left' : 'right']: '1.5rem' }}
                        >
                            <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
                            {SLIDES[currentSlide].badge}
                        </div>
                    )}
                    <div className="flex gap-2 pointer-events-auto z-20">
                        {SLIDES.map((_, idx) => (
                            <button key={idx} onClick={() => setCurrentSlide(idx)}
                                className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'w-8 bg-primary' : 'w-2 bg-white/50'}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Main Text Content */}
                <div
                    className={`flex flex-col justify-end lg:justify-center px-6 lg:pb-20 pb-12 pt-4 pointer-events-auto w-full lg:w-[55%] items-start ${isRTL ? 'text-right lg:pr-32 lg:pl-10' : 'text-left lg:pl-32 lg:pr-10'}`}
                >
                    {/* Slide Text */}
                    <div className="relative mb-6 lg:mb-12">
                        {/* Big number — outside the content edge */}
                        <span
                            className="hidden lg:block absolute top-0 text-[12rem] font-serif text-secondary/5 leading-none select-none"
                            style={{ [isRTL ? 'right' : 'left']: '-5rem' }}
                        >
                            0{currentSlide + 1}
                        </span>

                        {/* Tagline — decorative line always on the outer edge */}
                        <div className="flex items-center gap-4 mb-4">
                            <span className="h-px w-8 lg:w-16 bg-primary flex-shrink-0"></span>
                            <span key={`tag-${currentSlide}`} className="text-secondary font-medium tracking-[0.2em] text-xs lg:text-sm uppercase animate-in fade-in duration-500">
                                {SLIDES[currentSlide].tagline}
                            </span>
                        </div>

                        <h1 key={`title-${currentSlide}`} className="text-5xl sm:text-6xl lg:text-[5.5rem] font-serif font-semibold text-secondary leading-[0.9] lg:leading-[0.95] tracking-tight animate-in fade-in slide-in-from-bottom-8 duration-700">
                            {SLIDES[currentSlide].title} <br />
                            <span className="relative inline-block text-primary/90">
                                {SLIDES[currentSlide].subtitle}
                                <span
                                    className="absolute -bottom-2 lg:-bottom-4 w-full h-3 lg:h-6 bg-primary/10 -z-10 -rotate-1 rounded-full"
                                    style={{ [isRTL ? 'right' : 'left']: 0 }}
                                />
                            </span>
                        </h1>
                    </div>

                    <p key={`desc-${currentSlide}`} className="text-text-muted text-base lg:text-xl font-light max-w-md mb-8 lg:mb-12 leading-relaxed animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100 w-full">
                        {SLIDES[currentSlide].description}
                    </p>

                    {/* CTA Buttons — always aligned to the content side */}
                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                        {SLIDES[currentSlide].ctaText && (
                            <a
                                href={SLIDES[currentSlide].ctaUrl || '#'}
                                className="group flex items-center justify-center gap-3 bg-secondary text-primary px-8 py-4 rounded-full text-base font-medium shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                            >
                                {SLIDES[currentSlide].ctaText}
                                <CtaArrow size={20} className={`transition-transform ${isRTL ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
                            </a>
                        )}
                        {SLIDES[currentSlide].secondaryCtaText && (
                            <a
                                href={SLIDES[currentSlide].secondaryCtaUrl || '#'}
                                className="group hidden lg:flex items-center justify-center gap-3 bg-white border border-secondary/10 px-8 py-4 rounded-full text-secondary font-medium hover:bg-secondary/5 hover:border-secondary transition-all duration-300"
                            >
                                <Play size={18} className="fill-current" />
                                {SLIDES[currentSlide].secondaryCtaText}
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Desktop Navigation — bottom-right in LTR, bottom-left in RTL */}
            <div
                className="hidden lg:flex absolute bottom-12 z-20 gap-4"
                style={{ [isRTL ? 'left' : 'right']: '3rem' }}
            >
                <button onClick={prevSlide} className="w-12 h-12 rounded-full border border-secondary/10 flex items-center justify-center hover:bg-secondary hover:text-white transition-all duration-300">
                    <PrevIcon size={20} />
                </button>
                <button onClick={nextSlide} className="w-12 h-12 rounded-full border border-secondary/10 flex items-center justify-center hover:bg-secondary hover:text-white transition-all duration-300">
                    <NextIcon size={20} />
                </button>
            </div>

            {/* Desktop Trust Stats — bottom-left in LTR, bottom-right in RTL */}
            {TRUST_STATS.length > 0 && (
                <div
                    className="hidden lg:flex absolute bottom-12 z-20 items-center bg-white/40 backdrop-blur border border-white/50 px-6 py-3 rounded-full gap-6 text-secondary shadow-sm"
                    style={{ [isRTL ? 'right' : 'left']: '8rem' }}
                >
                    {TRUST_STATS.map((stat, idx) => (
                        <div key={stat.id} className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-sm">
                                {idx === 0 && <Star size={16} className="text-primary fill-primary" />}
                                <span className="font-semibold tracking-wide text-gray-900">{stat.label}</span>
                            </div>
                            {idx < TRUST_STATS.length - 1 && <div className="h-4 w-px bg-secondary/20"></div>}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};
